// TODO: MVP-CW convert into generic plot level indicator widget card
import { Divider, Stack, StackProps } from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BenchmarkType } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import {
  NCCard,
  NCCard2SectionLayout,
  NCCardComplianceFooter,
  NCCardComplianceFooterProps,
  NCCardHeader,
  NCCardHeaderProps,
  NCCardProps,
} from '@/components/NCCard/NCCard';
import { FactLinearGradient } from '@/components/NCFactCapsule/FactLinearGradient';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { isNullish } from '@/utils/booleans';

import { NoPlotData } from './NoPlotData';

const StatsTile = ({
  children,
  benchmark,
  explainer,
}: {
  children?: React.ReactNode;
  benchmark?: BenchmarkType | null;
  explainer?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  if (!benchmark && !explainer && !children) {
    return null;
  }

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='stats-tile'>
      {children}
      {(benchmark || explainer) && (
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          {benchmark && <BenchmarkPill benchmarkType={benchmark} />}
          {!isNullish(explainer) ? (
            <>
              <span className='typography-body1Bold'>{t('global.whatDoesThisMean')}</span>
              {explainer}
            </>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};

const GraphTile = ({
  children,
  graphHeaderProps,
  factLinearGradientProps,
  ...delegated
}: {
  children?: React.ReactNode;
  graphHeaderProps?: Partial<React.ComponentProps<typeof ChartHeading>>;
  factLinearGradientProps?: Partial<React.ComponentProps<typeof FactLinearGradient>>;
} & StackProps) => {
  if (isNullish(graphHeaderProps) && isNullish(factLinearGradientProps) && isNullish(children)) {
    return <NoPlotData />;
  }

  return (
    <Stack data-testid='graph-tile' {...delegated}>
      {graphHeaderProps && (
        <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
          <ChartHeading heading={graphHeaderProps?.heading ?? ''} {...graphHeaderProps} />
        </Stack>
      )}
      {children}
      {factLinearGradientProps && <FactLinearGradient {...factLinearGradientProps} />}
    </Stack>
  );
};

type PlotIndicatorProps = {
  cardProps?: Partial<NCCardProps>;
  headerProps?: Partial<NCCardHeaderProps>;
  footerProps?: Partial<NCCardComplianceFooterProps>;
  statProps?: Partial<React.ComponentProps<typeof StatsTile>>;
  graphProps?: Partial<React.ComponentProps<typeof GraphTile>>;
  nCCard2SectionLayoutProps?: Partial<React.ComponentProps<typeof NCCard2SectionLayout>>;
};

export const PlotIndicatorCard = ({
  cardProps,
  headerProps,
  footerProps,
  statProps,
  graphProps,
  nCCard2SectionLayoutProps,
}: PlotIndicatorProps) => {
  return (
    <NCCard {...cardProps}>
      <NCCardHeader {...headerProps} />
      <NCCard2SectionLayout {...nCCard2SectionLayoutProps}>
        <GraphTile {...graphProps} />
        <StatsTile {...statProps} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={footerProps?.logos ?? []} {...footerProps} />
    </NCCard>
  );
};
